import { Component, effect, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ClientService } from '../services/client.service';
import { AuthService } from '../services/auth.service';
import { APIUrl } from '../constant/api-call';
import * as _ from 'underscore';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PrimeNgModule } from '../shared/prime-ng.module';
import { ProjectService } from '../services/project.service';
import { DynamicEntryFormComponent } from '../dynamic-entry-form/dynamic-entry-form.component';

@Component({
  selector: 'app-project-details',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PrimeNgModule, RouterModule, DynamicEntryFormComponent],
  templateUrl: './project-details.component.html',
  styleUrl: './project-details.component.css',
})
export class ProjectDetailsComponent {
  @ViewChild(DynamicEntryFormComponent) dynamicForm!: DynamicEntryFormComponent;
  APIObj = APIUrl;
  public commonDataObject = {
    displayDialog: false,
    selectedProject: null as any
  }
  public dialogHeader: string = 'Submission Form';
  expenseApiData: any;
  chartData: any = {};
  activeChartType: string = 'expense';
  viewMode: string = 'chart';
  tableData: any[] = [];
  public chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          boxWidth: 20,
          padding: 5,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
            if (this.activeChartType === 'expense') {
              return `${label}: ₹ ${this.formatValue(value)} (${percentage}%)`;
            }
            return `${label}: ${this.formatValue(value)} (${percentage}%)`;
          },
        },
      },
    },
    cutout: '0%',
    elements: {
      arc: {
        borderWidth: 2,
        borderColor: '#ffffff',
      },
    },
  };

  constructor(
    private clientService: ClientService,
    private authService: AuthService,
    public projectService: ProjectService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
    effect(() => {
      this.getMasterData();
      this.getExpenseData();
    });
  }

  getExpenseData() {
    const payload = { project_id: this.projectService.selectedProject()?.project_id };
    this.clientService.postAPI(this.APIObj.PROJECT_REPORT, payload).subscribe((res: any) => {
      if (res && res.data && res.code === 'CSR_MS_OK_200') {
        this.expenseApiData = res.data;
        this.tableData = res.data.data;
        this.prepareChartData();
      } else {
        this.expenseApiData = {};
        this.tableData = [];
        this.chartData = {};
      }
    });
  }

  getMasterData() {
    this.projectService.getMasterData();
    const projectData = sessionStorage.getItem('selected_project');
    if (projectData) {
      this.commonDataObject.selectedProject = JSON.parse(projectData);
    }
  }

  getOverallExpenseData(): { key: string; value: number }[] {
    if (this.expenseApiData?.year_wise_expense) {
      return Object.keys(this.expenseApiData.year_wise_expense).map((key) => ({
        key,
        value: this.expenseApiData.year_wise_expense[key],
      }));
    }
    return [];
  }

  getTotalForms(): number {
    return this.tableData.reduce((sum, item) => sum + (item.total_forms_filled || 0), 0);
  }

  getTotalExpense(): number {
    return this.tableData.reduce((sum, item) => sum + (item.expense || 0), 0);
  }

  prepareChartData() {
    const formWiseData: { [key: string]: number } = {};
    this.tableData.forEach((item) => {
      const formName = item.form_name;
      formWiseData[formName] = (formWiseData[formName] || 0) + item.total_forms_filled;
    });

    const yearWiseExpense: { [key: string]: number } = this.expenseApiData?.year_wise_expense ?? {};

    const yearWiseFormSubmissions: { [key: string]: number } = {};
    this.tableData.forEach((item) => {
      const year = item?.year_value?.toString();
      yearWiseFormSubmissions[year] = (yearWiseFormSubmissions[year] || 0) + item.total_forms_filled;
    });

    this.chartData = {
      formWise: {
        labels: Object.keys(formWiseData),
        datasets: [
          {
            data: Object.values(formWiseData),
            backgroundColor: this.generateColors(Object.keys(formWiseData).length),
            borderColor: 'white',
            borderWidth: 2,
            hoverOffset: 15,
            hoverBorderColor: '#ffffff',
            hoverBorderWidth: 3,
          },
        ],
      },
      yearWiseExpense: {
        labels: Object.keys(yearWiseExpense),
        datasets: [
          {
            data: Object.values(yearWiseExpense),
            backgroundColor: this.generateColors(Object.keys(yearWiseExpense).length),
            borderColor: 'white',
            borderWidth: 2,
            hoverOffset: 15,
            hoverBorderColor: '#ffffff',
            hoverBorderWidth: 3,
          },
        ],
      },
      yearWiseForms: {
        labels: Object.keys(yearWiseFormSubmissions),
        datasets: [
          {
            data: Object.values(yearWiseFormSubmissions),
            backgroundColor: this.generateColors(Object.keys(yearWiseFormSubmissions).length),
            borderColor: 'white',
            borderWidth: 2,
            hoverOffset: 15,
            hoverBorderColor: '#ffffff',
            hoverBorderWidth: 3,
          },
        ],
      },
    };

    this.cdr.detectChanges();
  }

  generateColors(count: number): string[] {
    const colors = [
      '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
      '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
      '#84cc16', '#0891b2', '#a855f7', '#22c55e', '#eab308'
    ];
    return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
  }
  setChartType(type: string) {
    this.activeChartType = type;
  }

  toggleViewMode(mode: string) {
    this.viewMode = mode;
    this.cdr.detectChanges();
  }

  onFormNameChange(name: any) {
    this.dialogHeader = name;
  }

  openSubmissionForm() {
    this.commonDataObject.displayDialog = true;
    if (this.dynamicForm) {
      this.dynamicForm.resetForm();
      this.dynamicForm.autoSelectSingleForm();
    }
  }

  onFormSaved() {
    this.commonDataObject.displayDialog = false;
  }

  isNumber(value: any): boolean {
    return !isNaN(value) && value !== null && value !== '' && value !== '-';
  }

  formatValue(value: any): string {
    if (this.isNumber(value)) {
      return Number(value).toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }
    return value || '-';
  }
}